import type { InvoiceRecipientOption } from '@tandemone/types'
import { useQuery } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import { useNavigate } from 'react-router'

import { Button } from '@/components/ui/button'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList
} from '@/components/ui/combobox'
import { DatePicker } from '@/components/ui/date-picker'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { StaffWriteGate } from '@/domains/setup'
import { cn } from '@/lib/utils'

import { searchInvoiceRecipients } from './api'
import { type PaymentTerm, PAYMENT_TERMS } from './invoice-form-context'
import { InvoiceFormProvider } from './invoice-form-provider'
import { useInvoiceForm } from './use-invoice-form'
import { formatMoney } from './utils'

const CURRENCIES = ['usd', 'cad', 'eur', 'gbp', 'jpy']

export function InvoiceForm() {
  return (
    <InvoiceFormProvider>
      <FormBody />
    </InvoiceFormProvider>
  )
}

function FormBody() {
  const navigate = useNavigate()
  const { state, derived, dispatch, submit } = useInvoiceForm()

  const recipientsQuery = useQuery({
    queryKey: ['invoice-recipients'],
    queryFn: ({ signal }) => searchInvoiceRecipients('', signal),
    staleTime: 60_000
  })
  const recipientOptions = recipientsQuery.data?.items ?? []

  return (
    <form
      onSubmit={event => {
        event.preventDefault()
        submit('draft')
      }}
      className='flex w-full flex-col gap-6'
    >
      <section className='flex flex-col gap-4 rounded-xl border border-border bg-card p-4 sm:p-6'>
        <h2 className='text-[13px] font-semibold text-ink'>Recipient</h2>
        <Field label='Bill to' htmlFor='recipient'>
          <Combobox
            items={recipientOptions}
            value={state.recipient}
            onValueChange={(next: InvoiceRecipientOption | null) =>
              dispatch({ type: 'SET_RECIPIENT', payload: next })
            }
            itemToStringLabel={(option: InvoiceRecipientOption) => option.name}
            itemToStringValue={(option: InvoiceRecipientOption) => option.name}
          >
            <ComboboxInput
              id='recipient'
              autoComplete='organization'
              placeholder={
                recipientsQuery.isLoading
                  ? 'Loading companies…'
                  : 'Search companies…'
              }
            />
            <ComboboxContent>
              <ComboboxEmpty>No companies match.</ComboboxEmpty>
              <ComboboxList>
                {(option: InvoiceRecipientOption) => (
                  <ComboboxItem key={option.companyId} value={option}>
                    <span className='flex w-full flex-col'>
                      <span className='text-[13px] text-ink'>
                        {option.name}
                      </span>
                      {option.location ? (
                        <span className='text-[11px] text-ink-muted'>
                          {option.location}
                        </span>
                      ) : null}
                    </span>
                  </ComboboxItem>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        </Field>
        <p className='text-[12px] text-ink-muted'>
          The billed company and its members are notified in-app and by email
          when you send this invoice.
        </p>
        <Field label='PO number' htmlFor='po'>
          <Input
            id='po'
            value={state.purchaseOrderNumber}
            onChange={event =>
              dispatch({
                type: 'SET_FIELD',
                field: 'purchaseOrderNumber',
                value: event.target.value
              })
            }
          />
        </Field>
      </section>

      <section className='flex flex-col gap-4 rounded-xl border border-border bg-card p-4 sm:p-6'>
        <h2 className='text-[13px] font-semibold text-ink'>Details</h2>
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
          <Field label='Payment term' htmlFor='term'>
            <Combobox
              items={PAYMENT_TERMS}
              value={
                PAYMENT_TERMS.find(t => t.value === state.paymentTerm) ?? null
              }
              onValueChange={(
                next: { value: PaymentTerm; label: string; days: number } | null
              ) => next && dispatch({ type: 'SET_TERM', value: next.value })}
              itemToStringLabel={term => term?.label ?? ''}
              itemToStringValue={term => term?.label ?? ''}
            >
              <ComboboxInput id='term' placeholder='Net 30' />
              <ComboboxContent>
                <ComboboxList>
                  {term => (
                    <ComboboxItem key={term.value} value={term}>
                      {term.label}
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
          </Field>
          <Field label='Due date' htmlFor='dueAt'>
            <DatePicker
              id='dueAt'
              value={state.dueAt}
              onChange={value => dispatch({ type: 'SET_DUE_DATE', value })}
            />
          </Field>
        </div>
        <Field label='Currency' htmlFor='currency'>
          <Combobox
            items={CURRENCIES}
            value={state.currency}
            onValueChange={(next: string | null) =>
              next &&
              dispatch({ type: 'SET_FIELD', field: 'currency', value: next })
            }
            itemToStringLabel={(code: string) => code.toUpperCase()}
            itemToStringValue={(code: string) => code.toUpperCase()}
          >
            <ComboboxInput id='currency' />
            <ComboboxContent>
              <ComboboxList>
                {(code: string) => (
                  <ComboboxItem key={code} value={code}>
                    {code.toUpperCase()}
                  </ComboboxItem>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        </Field>
      </section>

      <section className='flex flex-col gap-4 rounded-xl border border-border bg-card p-4 sm:p-6'>
        <div className='flex items-center justify-between'>
          <h2 className='text-[13px] font-semibold text-ink'>Line items</h2>
          {/* beside the title on desktop, under the list on a phone, where a
              small button welded to a heading is unreachable. */}
          <AddItemButton
            className='hidden sm:inline-flex'
            onClick={() => dispatch({ type: 'ADD_LINE_ITEM' })}
          />
        </div>
        <div className='flex flex-col gap-3'>
          {state.lineItems.map(item => (
            <div
              key={item.key}
              className='grid grid-cols-[1fr_auto] items-end gap-2 sm:grid-cols-[1fr_70px_110px_auto]'
            >
              <Field
                className='col-span-2 sm:col-span-1'
                label='Description'
                htmlFor={`desc-${item.key}`}
              >
                <Input
                  id={`desc-${item.key}`}
                  value={item.description}
                  placeholder='Consulting services'
                  onChange={event =>
                    dispatch({
                      type: 'UPDATE_LINE_ITEM',
                      key: item.key,
                      patch: { description: event.target.value }
                    })
                  }
                />
              </Field>
              <Field label='Qty' htmlFor={`qty-${item.key}`}>
                <Input
                  id={`qty-${item.key}`}
                  type='number'
                  min='0'
                  value={item.quantity}
                  onChange={event =>
                    dispatch({
                      type: 'UPDATE_LINE_ITEM',
                      key: item.key,
                      patch: { quantity: event.target.value }
                    })
                  }
                />
              </Field>
              <Field label='Unit price' htmlFor={`price-${item.key}`}>
                <Input
                  id={`price-${item.key}`}
                  type='number'
                  min='0'
                  step='0.01'
                  value={item.unitPrice}
                  onChange={event =>
                    dispatch({
                      type: 'UPDATE_LINE_ITEM',
                      key: item.key,
                      patch: { unitPrice: event.target.value }
                    })
                  }
                />
              </Field>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='col-start-2 row-start-2 sm:col-auto sm:row-auto'
                aria-label='Remove line item'
                disabled={state.lineItems.length === 1}
                onClick={() =>
                  dispatch({ type: 'REMOVE_LINE_ITEM', key: item.key })
                }
              >
                <Trash2 className='size-4' />
              </Button>
            </div>
          ))}
        </div>

        <AddItemButton
          className='w-full sm:hidden'
          onClick={() => dispatch({ type: 'ADD_LINE_ITEM' })}
        />

        <div className='grid grid-cols-1 gap-4 border-t border-border pt-4 sm:grid-cols-2'>
          <Field label='Tax amount' htmlFor='tax'>
            <Input
              id='tax'
              type='number'
              min='0'
              step='0.01'
              value={state.taxAmount}
              onChange={event =>
                dispatch({
                  type: 'SET_FIELD',
                  field: 'taxAmount',
                  value: event.target.value
                })
              }
            />
          </Field>
          <Field label='Shipping' htmlFor='shipping'>
            <Input
              id='shipping'
              type='number'
              min='0'
              step='0.01'
              value={state.shippingAmount}
              onChange={event =>
                dispatch({
                  type: 'SET_FIELD',
                  field: 'shippingAmount',
                  value: event.target.value
                })
              }
            />
          </Field>
        </div>

        <dl className='ml-auto flex w-full flex-col gap-1.5 text-[13px] sm:w-56'>
          <Row
            label='Subtotal'
            value={formatMoney(derived.subtotal, state.currency)}
          />
          <Row
            label='Tax'
            value={formatMoney(derived.taxAmount, state.currency)}
          />
          <Row
            label='Shipping'
            value={formatMoney(derived.shippingAmount, state.currency)}
          />
          <div className='mt-1 flex items-center justify-between border-t border-accent pt-2'>
            <dt className='font-semibold text-ink'>Total</dt>
            <dd className='font-semibold text-accent dark:text-accent-mid'>
              {formatMoney(derived.total, state.currency)}
            </dd>
          </div>
        </dl>
      </section>

      <section className='flex flex-col gap-4 rounded-xl border border-border bg-card p-4 sm:p-6'>
        <Field label='Payment Information' htmlFor='notes'>
          <Textarea
            id='notes'
            value={state.notes}
            placeholder='Payment terms, additional instructions or other notes.'
            onChange={event =>
              dispatch({
                type: 'SET_FIELD',
                field: 'notes',
                value: event.target.value
              })
            }
          />
        </Field>
      </section>

      {state.error ? (
        <div className='rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-300'>
          {state.error}
        </div>
      ) : null}

      {/* reversed on a phone so the primary action is the first thing under
          the form and Cancel is last. */}
      <div className='flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <Button
          type='button'
          variant='ghost'
          className='w-full sm:w-auto'
          onClick={() => navigate('/invoices')}
          disabled={state.submitting}
        >
          Cancel
        </Button>
        <div className='flex flex-col-reverse gap-2 sm:flex-row sm:items-center'>
          <StaffWriteGate>
            <Button
              type='submit'
              variant='outline'
              className='w-full sm:w-auto'
              disabled={state.submitting || !derived.isValid}
            >
              Save as draft
            </Button>
          </StaffWriteGate>
          <StaffWriteGate>
            <Button
              type='button'
              className='w-full sm:w-auto'
              disabled={state.submitting || !derived.isValid}
              onClick={() => submit('send')}
            >
              {state.submitting ? 'Saving…' : 'Send invoice'}
            </Button>
          </StaffWriteGate>
        </div>
      </div>
    </form>
  )
}

function AddItemButton({
  className,
  onClick
}: {
  className?: string
  onClick: () => void
}) {
  return (
    <Button
      type='button'
      variant='outline'
      size='sm'
      className={className}
      onClick={onClick}
    >
      <Plus className='size-3.5' />
      Add item
    </Button>
  )
}

function Field({
  label,
  htmlFor,
  className,
  children
}: {
  label: string
  htmlFor: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={htmlFor} className='text-[12px] text-ink'>
        {label}
      </Label>
      {children}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex items-center justify-between'>
      <dt className='text-ink-muted'>{label}</dt>
      <dd className='text-ink-soft'>{value}</dd>
    </div>
  )
}
